import { propEq } from "ramda";
import { TAX_CALCULATION_TYPES } from "api/invoicing/constants";
import { localizeTransactionStatus } from "components/Transaction/utils";

export const DAY_DATE_FORMAT = "MMMM do";

export const isTaxInclusive = (calculationType) =>
  calculationType === TAX_CALCULATION_TYPES.INCLUSIVE;

export const showTaxInclusiveMessage = (transaction) => {
  const hasTaxAmount = Boolean(transaction?.transaction_data?.summary?.tax?.total_amount);
  return hasTaxAmount && isTaxInclusive(transaction?.transaction_data?.summary?.tax_calculation);
};

/**
 * Labels are tokens for translation process.
 */
export const INITIAL_COMPLETED_SELECTED_TYPES = [
  { value: "user_purchase,user_purchase_escrow", label: "purchase", selected: false },
  { value: "user_purchase_recipient", label: "sale", selected: false },
  { value: "token_purchase", label: "token_purchase", selected: false },
  { value: "token_convert", label: "token_convert", selected: false },
  { value: "payout", label: "payout", selected: false },
  { value: "refund", label: "refund", selected: false },
  { value: "deposit", label: "deposit", selected: false },
  { value: "balance_transfer", label: "balance_transfer", selected: false },
];

export const INITIAL_PENDING_SELECTED_TYPES = [
  { value: "user_purchase,user_purchase_escrow", label: "purchase", selected: false },
  { value: "payout", label: "payout", selected: false },
  { value: "deposit", label: "deposit", selected: false },
];

export const INITIAL_COMPLETED_STATUSES = ["processed", "payout-failed"];

export const INITIAL_PENDING_SELECTED_STATUSES = ["pending"];

export const isItemSelected = propEq("selected", true);
export const isItemUnselected = propEq("selected", false);

export const reduceSelectedFilters = (selectedFilters) =>
  selectedFilters.reduce((reducedFilters, selectedFilter) => {
    if (selectedFilter.selected) {
      reducedFilters.push(selectedFilter.value);
    }
    return reducedFilters;
  }, []);

/**
 * getFinalFilterSets
 * @argument {Filters} filters
 * @argument {Array.<string>} transactionTypesToOmit
 * @returns {Filters}
 */
export const getFinalFilterSets = (filters, transactionTypesToOmit = []) => {
  if (!transactionTypesToOmit.length) {
    return filters;
  }

  const setNames = Object.keys(filters);

  let newFilters = filters;
  setNames.forEach((setName) => {
    newFilters[setName].types = newFilters[setName].types.filter(
      (type) => !transactionTypesToOmit.includes(type.value)
    );
  });

  return newFilters;
};

/**
 * @typedef FormattedTransactionItem
 * @property {string | undefined} description Description of transaction being displayed to end-user
 * @property {string} type Type of transaction (i.e. Sale, Purchase, Payout, etc) or the date, if transaction is pending
 * @property {number | string | undefined} amount The total amount of the transaction
 * @property {string | undefined} currency Currency type for currency formatter
 * @property {string} amountColor Green or red, depending on the transaction type
 * @property {string} [localizedStatus] currently only used in payout invoices to handle some specific messaging to user, but available should we use it elsewhere
 */

/**
 * @function modelDepositTransactionType
 * @argument {Transaction} transaction
 * @argument {function} formatMessage
 * @returns {FormattedTransactionItem}
 */
const modelDepositTransactionType = (transaction, formatMessage) => {
  return {
    description: transaction.transaction_description,
    type: formatMessage({
      defaultMessage: "Deposit",
      description: "Money transaction type",
    }),
    amount: transaction.transaction_data?.deposit?.request_amount,
    currency: transaction.transaction_data?.deposit?.request_currency,
    amountColor: "successText",
  };
};

/**
 * @function modelUserPurchaseTransactionType
 * @argument {Transaction} transaction
 * @argument {function} formatMessage
 * @returns {FormattedTransactionItem}
 */
const modelUserPurchaseTransactionType = (transaction, formatMessage) => {
  return {
    description: transaction.transaction_description,
    type: formatMessage({
      defaultMessage: "Purchase",
      description: "Money transaction type",
    }),
    amount: transaction.transaction_data?.summary?.total_amount,
    currency: transaction.transaction_data?.summary?.currency,
    amountColor: "errorText",
  };
};

/**
 * @function modelUserPurchaseRecipientTransactionType
 * @argument {Transaction} transaction
 * @argument {function} formatMessage
 * @returns {FormattedTransactionItem}
 */
const modelUserPurchaseRecipientTransactionType = (transaction, formatMessage) => {
  return {
    description: transaction.transaction_description,
    type: formatMessage({
      defaultMessage: "Sale",
      description: "Money transaction type",
    }),
    amount: transaction.transaction_data?.total_received,
    currency: transaction.transaction_data?.currency,
    amountColor: "successText",
  };
};

/**
 * @function modelPayoutTransactionType
 * @argument {Transaction} transaction
 * @argument {function} formatMessage
 * @returns {FormattedTransactionItem}
 */
const modelPayoutTransactionType = (transaction, formatMessage) => {
  const localizedStatus = localizeTransactionStatus(transaction, formatMessage);
  const {
    payout_amount_less_fee,
    payout_amount_less_fee_currency,
    currency
  } = transaction.transaction_data || {};
  return {
    description: transaction.transaction_description,
    type: formatMessage({
      defaultMessage: "Payout",
      description: "Money transaction type",
    }),
    amount: payout_amount_less_fee,
    currency: payout_amount_less_fee_currency || currency,
    amountColor: "successText",
    localizedStatus: localizedStatus || null, // localizedStatus is currently only used in payout invoices to handle some specific messaging to user, but available should we use it elsewhere
  };
};

/**
 * @function modelTokenPurchaseTransactionType
 * @argument {Transaction} transaction
 * @argument {function} formatMessage
 * @returns {FormattedTransactionItem}
 */
const modelTokenPurchaseTransactionType = (transaction, formatMessage) => {
  const description = transaction.transaction_description;
  const type = formatMessage({
    defaultMessage: "Tokens issued",
    description: "Money transaction type",
  });

  if (transaction.transaction_data?.source_payment_method_provider === "pobo") {
    return {
      description,
      type,
      amount: transaction.transaction_data?.token_exchange?.user_received_amount,
      currency: transaction.transaction_data?.token_exchange?.user_received_currency,
      amountColor: "successText",
    };
  } else {
    return {
      description,
      type,
      amount: transaction.transaction_data?.total_amount,
      currency: transaction.transaction_data?.currency,
      amountColor: "errorText",
    };
  }
};

/**
 * @function modelTokenConvertTransactionType
 * @argument {Transaction} transaction
 * @argument {function} formatMessage
 * @returns {FormattedTransactionItem}
 */
const modelTokenConvertTransactionType = (transaction, formatMessage) => {
  return {
    description: transaction.transaction_description,
    type: formatMessage({
      defaultMessage: "Token convert",
      description: "Money transaction type",
    }),
    amount: transaction.transaction_data?.token_exchange?.user_received_amount,
    currency: transaction.transaction_data?.token_exchange?.user_received_currency,
    amountColor: "successText",
  };
};

/**
 * @function modelRefundTransactionType
 * @argument {Transaction} transaction
 * @argument {function} formatMessage
 * @returns {FormattedTransactionItem}
 */
const modelRefundTransactionType = (transaction, formatMessage) => {
  const isPobo = transaction.transaction_data.refund_payment_methods.find(
    (refundPm) => refundPm.source_payment_method_provider === "pobo"
  );
  return {
    description: transaction.transaction_description,
    type: formatMessage({
      defaultMessage: "Refund",
      description: "Money transaction type",
    }),
    amount: transaction.transaction_data?.total_amount,
    currency: transaction.transaction_data?.amount_currency,
    amountColor: isPobo ? "errorText" : "successText",
  };
};

/**
 * @function modelBalanceTransferTransactionType
 * @argument {Transaction} transaction
 * @argument {function} formatMessage
 * @returns {FormattedTransactionItem}
 */
const modelBalanceTransferTransactionType = (transaction, accountId, formatMessage) => {
  let wallets, wallet, walletId;
  switch (transaction.transaction_data.balance_transfer_type) {
    case "fund_user":
      walletId = transaction.transaction_data?.transfer_items[0].destination_wallet_id;
      wallet = transaction.transaction_data.wallets[walletId];
      return {
        description: wallet.description,
        type: formatMessage({
          defaultMessage: "Credit",
          description: "Money transaction type",
        }),
        amount: transaction.transaction_data?.transfer_items[0].amount,
        currency: transaction.transaction_data?.transfer_items[0].currency,
        amountColor: "successText",
      };
    case "token_convert_payout_reversal":
      const tokenDebitReversal = transaction.transaction_data.transfer_items.find(
        (ti) => ti.subitem_type === "token_convert_virtual_debit_reversal"
      );
      walletId = tokenDebitReversal.destination_wallet_id;
      wallet = transaction.transaction_data.wallets[walletId];

      return {
        description: wallet.description,
        type: formatMessage({
          defaultMessage: "Token Credit",
          description: "Money transaction type",
        }),
        amount: tokenDebitReversal.amount,
        currency: tokenDebitReversal.currency,
        amountColor: "successText",
      };
    case "merge_account":
      const mergeAccountBalanceTransfer = transaction.transaction_data.transfer_items[0];
      wallets = transaction.transaction_data.wallets;

      // assume the current user is someone who is credited the amount
      walletId = mergeAccountBalanceTransfer.destination_wallet_id;

      // if current account is the source account, it implies this user is the one who
      // transferred money "debit"
      if (mergeAccountBalanceTransfer.source_account_id === accountId) {
        walletId = mergeAccountBalanceTransfer.source_wallet_id;
      }
      wallet = wallets[walletId];

      const mergeAccountBalanceTransferType =
        wallet.net_change > 0
          ? formatMessage({
              defaultMessage: "Credit",
              description: "Money transaction type",
            })
          : formatMessage({
              defaultMessage: "Debit",
              description: "Money transaction type",
            });

      return {
        description: wallet.description,
        type: mergeAccountBalanceTransferType,
        amount: mergeAccountBalanceTransfer.amount,
        currency: mergeAccountBalanceTransfer.currency,
        amountColor: wallet.net_change > 0 ? "successText" : "errorText",
      };
    default:
      return {
        description: "Transaction",
        type: formatMessage({
          defaultMessage: "Unknown",
          description: "Money transaction type",
        }),
        amount: formatMessage({
          defaultMessage: "N/A",
          description: "Not applicable",
        }),
        amountColor: "primaryText",
      };
  }
};

/**
 * formatTransactionBasedOnTransactionType
 * @argument {Transaction} transaction
 * @argument {function} formatMessage
 * @returns {FormattedTransactionItem}
 */
export const formatTransactionBasedOnTransactionType = (transaction, accountId, formatMessage) => {
  switch (transaction.transaction_type) {
    case "balance_transfer":
      return modelBalanceTransferTransactionType(transaction, accountId, formatMessage);
    case "deposit":
      return modelDepositTransactionType(transaction, formatMessage);
    case "user_purchase":
    case "user_purchase_escrow":
      return modelUserPurchaseTransactionType(transaction, formatMessage);
    case "user_purchase_recipient":
      return modelUserPurchaseRecipientTransactionType(transaction, formatMessage);
    case "payout":
      return modelPayoutTransactionType(transaction, formatMessage);
    case "token_purchase":
      return modelTokenPurchaseTransactionType(transaction, formatMessage);
    case "token_convert":
      return modelTokenConvertTransactionType(transaction, formatMessage);
    case "refund":
      return modelRefundTransactionType(transaction, formatMessage);
    default:
      return {
        description: "Transaction",
        type: formatMessage({
          defaultMessage: "Unknown",
          description: "Money transaction type",
        }),
        amount: formatMessage({
          defaultMessage: "N/A",
          description: "Not applicable",
        }),
        amountColor: "primaryText",
      };
  }
};

/**
 * groupTransactionsByDay
 * @param {Transaction[]} transactions
 */
export const groupTransactionsByDay = (transactions = [], formatDate) => {
  const byDay = transactions.reduce((group, transaction) => {
    const { transaction_date } = transaction;
    const date = formatDate(new Date(transaction_date), {
      month: "long",
      day: "numeric",
      year: "numeric",
    });

    group[date] = group[date] ?? [];
    group[date].push(transaction);

    return group;
  }, {});

  return byDay;
};

/**
 * @typedef BreakdownSummaryTransaction
 * @type {Object}
 * @property {array} llineItems
 * @property {array} summaryItems
 * @property {number} total
 * @property {string} currency
 */

/**
 * @function modelDepositBreakdownSummary
 * @param {object} transaction
 * @param {function} formatMessage
 * @returns {BreakdownSummaryTransaction}
 */
const modelDepositBreakdownSummary = (transaction, formatMessage) => {
  const { request_amount, fee_amount, total_amount, deposit, currency } =
    transaction.transaction_data;

  const summaryItems = [
    {
      title: formatMessage({
        defaultMessage: "Requested",
        description: "Field label",
      }),
      value: request_amount,
      currency: deposit?.request_currency,
    },
    {
      title: formatMessage({
        defaultMessage: "Fees",
        description: "Field label",
      }),
      value: fee_amount,
      currency: deposit?.fee_currency,
    },
  ];

  return {
    summaryItems,
    lineItems: [],
    total: total_amount,
    currency: currency,
  };
};

/**
 * @function modelUserPurchaseBreakdownSummary
 * @param {object} transaction
 * @param {function} formatMessage
 * @returns {BreakdownSummaryTransaction}
 */
const modelUserPurchaseBreakdownSummary = (transaction, formatMessage) => {
  const { summary, line_items } = transaction.transaction_data; // @type {TxnBuyerData}
  const { subtotal, total_amount, tax, currency, tax_calculation } = summary;

  const lineItems = Object.values(line_items || {})
    .sort((a, b) => (a.sort_order < b.sort_order ? -1 : 1))
    .map((li) => ({ title: li.description, value: li.amount, currency: li.currency }));
  const summaryItems = [];

  if (tax && tax.total_amount && !isTaxInclusive(tax_calculation)) {
    summaryItems.push({
      title: formatMessage({
        defaultMessage: "Subtotal",
        description: "Field label",
      }),
      value: subtotal.total_amount,
      currency: subtotal.currency,
    });
    summaryItems.push({
      title: formatMessage({
        defaultMessage: "Tax",
        description: "Field label",
      }),
      value: tax.total_amount,
      currency: tax.currency,
    });
  }

  return {
    summaryItems,
    lineItems,
    total: total_amount,
    currency,
  };
};

/**
 * @function modelUserPurchaseRecipientBreakdownSummary
 * @param {object} transaction
 * @param {function} formatMessage
 * @returns {BreakdownSummaryTransaction}
 */
const modelUserPurchaseRecipientBreakdownSummary = (transaction, formatMessage) => {
  const { total_received, total_fees_paid, total_received_less_fees, recipient_items, currency } =
    transaction.transaction_data; // @type {TxnSellerData}

  const lineItems = (recipient_items || []).map((ri) => ({
    title: ri.description,
    value: ri.amount_received,
    currency: ri.currency,
  }));
  const summaryItems = [];

  // TODO: #currency verify that this currency belongs to these currency amounts
  if (total_fees_paid) {
    summaryItems.push({
      title: formatMessage({
        defaultMessage: "Subtotal",
        description: "Field label",
      }),
      value: total_received,
      currency,
    });
    summaryItems.push({
      title: formatMessage({
        defaultMessage: "Transaction fees",
        description: "Field label",
      }),
      value: total_fees_paid,
      currency,
    });
  }

  return {
    summaryItems,
    lineItems,
    total: total_received_less_fees,
    currency,
  };
};

/**
 * @function modelPayoutBreakdownSummary
 * @param {object} transaction
 * @param {function} formatMessage
 * @returns {BreakdownSummaryTransaction}
 */
const modelPayoutBreakdownSummary = (transaction, formatMessage) => {
  const {
    payout_total,
    payout_total_currency,
    payout_fee,
    payout_fee_currency,
    payout_amount_less_fee,
    payout_amount_less_fee_currency
  } = transaction.transaction_data; // @type {TxnPurchaseUserData}

  return {
    summaryItems: [
      {
        title: formatMessage({
          defaultMessage: "Requested",
          description: "Field label",
        }),
        value: payout_total,
        currency: payout_total_currency,
      },
      {
        title: formatMessage({
          defaultMessage: "Fees",
          description: "Field label",
        }),
        value: payout_fee,
        currency: payout_fee_currency,
      },
    ],
    lineItems: [],
    total: payout_amount_less_fee,
    currency: payout_amount_less_fee_currency,
  };
};

/**
 * @function modelTokenPurchaseBreakdownSummary
 * @param {object} transaction
 * @param {function} formatMessage
 * @returns {BreakdownSummaryTransaction}
 */
const modelTokenPurchaseBreakdownSummary = (transaction, formatMessage) => {
  const { source_payment_method_provider, summary } = transaction.transaction_data; // @type {TokenPurchaseData}
  if (source_payment_method_provider === "pobo") {
    /* Don't show a breakdown for 'pobo' purchases */
    return null;
  }

  const {
    subtotal_amount,
    total_amount,
    tilia_fee_amount,
    publisher_fee_amount,
    publisher_fee_description,
    tax_amount,
    currency,
  } = transaction.transaction_data; // @type {TokenPurchaseData}

  const summaryItems = [];
  const lineItems = [
    {
      title: formatMessage({
        defaultMessage: "Token purchase",
        description: "Field label",
      }),
      value: subtotal_amount,
      currency,
    },
  ];

  if (publisher_fee_amount || tilia_fee_amount || tax_amount) {
    summaryItems.push({
      title: formatMessage({
        defaultMessage: "Subtotal",
        description: "Field label",
      }),
      value: subtotal_amount,
      currency,
    });
  }
  if (publisher_fee_amount) {
    summaryItems.push({
      title:
        publisher_fee_description ||
        formatMessage({
          defaultMessage: "Fees",
          description: "Field label",
        }),
      value: publisher_fee_amount,
      currency,
    });
  }
  if (tilia_fee_amount) {
    summaryItems.push({
      title: formatMessage({
        defaultMessage: "Tilia fees",
        description: "Field label",
      }),
      value: tilia_fee_amount,
      currency,
    });
  }
  if (tax_amount && !isTaxInclusive(summary?.tax_calculation)) {
    summaryItems.push({
      title: formatMessage({
        defaultMessage: "Tax",
        description: "Field label",
      }),
      value: tax_amount,
      currency,
    });
  }

  return {
    summaryItems,
    lineItems,
    total: total_amount,
    currency,
  };
};

/**
 * @function modelRefundBreakdownSummary
 * @param {object} transaction
 * @param {function} formatMessage
 * @returns {BreakdownSummaryTransaction}
 */
const modelRefundBreakdownSummary = (transaction, formatMessage) => {
  const {
    refund_line_items,
    subtotal_amount,
    tax_total_amount,
    total_amount,
    amount_currency,
    summary,
  } = transaction.transaction_data; // @type {RefundBuyerData}

  const summaryItems = [];
  const lineItems = (refund_line_items || []).map((ri) => ({
    title: ri.description,
    value: ri.total_amount,
    currency: ri.currency,
  }));
  if (tax_total_amount && !isTaxInclusive(summary?.tax_calculation)) {
    summaryItems.push({
      title: formatMessage({
        defaultMessage: "Subtotal",
        description: "Field label",
      }),
      value: subtotal_amount,
      currency: amount_currency,
    });
    summaryItems.push({
      title: formatMessage({
        defaultMessage: "Tax",
        description: "Field label",
      }),
      value: tax_total_amount,
      currency: amount_currency,
    });
  }

  return {
    summaryItems,
    lineItems,
    total: total_amount,
    currency: amount_currency,
  };
};

/**
 * @function modelTransactionBreakdownSummary
 * @param {object} transaction 
 * @param {function} formatMessage 
 * @returns {BreakdownSummaryTransaction}
 */
export const modelTransactionBreakdownSummary = (transaction, formatMessage) => {
  switch (transaction.transaction_type) {
    case "deposit":
      return modelDepositBreakdownSummary(transaction, formatMessage);
    case "user_purchase":
    case "user_purchase_escrow":
      return modelUserPurchaseBreakdownSummary(transaction, formatMessage);
    case "user_purchase_recipient":
      return modelUserPurchaseRecipientBreakdownSummary(transaction, formatMessage);
    case "payout":
      return modelPayoutBreakdownSummary(transaction, formatMessage);
    case "token_purchase":
      return modelTokenPurchaseBreakdownSummary(transaction, formatMessage);
    case "refund":
      return modelRefundBreakdownSummary(transaction, formatMessage);
    case "token_convert":
    default:
      return null;
  }
};

/**
 * @function processFlowConfigInitialFilters
 * @param {Array<object>} filterOptions
 * @param {string} txnHistoryFiltersStr
 */
export const processFlowConfigInitialFilters = (filterOptions, txnHistoryFiltersStr) => {
  try {
    const selectedFilterLabels = txnHistoryFiltersStr.split(","); // comma separated string
    const modifiedOptions = filterOptions.map(fo => ({ ...fo })); // not modifiying input
    modifiedOptions.forEach(filterOption => {
      if (filterOption.label && selectedFilterLabels.includes(filterOption.label)) {
        filterOption.selected = true;
      }
    });
    return modifiedOptions;
  } catch {
    return filterOptions; // safety against bad inputs
  }
};